import React, { useState } from 'react';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useFetch } from '../../hooks';
import { safeArray } from '../../utils/helpers';

const RecordContributionForm = ({ chamaId, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    member_id: '',
    amount: '',
    payment_method: 'mpesa',
    reference_number: '',
    notes: '',
    contribution_date: new Date().toISOString().split('T')[0],
  });

  const { data: membersData } = useFetch(`/chamas/${chamaId}/members`);
  const members = safeArray(membersData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErrors({});

      await api.post('/contributions', {
        ...formData,
        chama_id: chamaId,
      });

      onSuccess?.();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to record contribution' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
          {errors.general}
        </div>
      )}

      <Select
        label="Member"
        name="member_id"
        value={formData.member_id}
        onChange={handleChange}
        options={members.map((m) => ({
          value: m.id,
          label: m.user?.name || 'Unknown Member',
        }))}
        error={errors.member_id?.[0]}
        required
      />

      <Input
        label="Amount (KES)"
        name="amount"
        type="number"
        value={formData.amount}
        onChange={handleChange}
        error={errors.amount?.[0]}
        placeholder="e.g., 1000"
        required
      />

      <div>
        <span className="block text-sm font-medium text-gray-700 mb-2">Payment Method</span>
        <div className="grid grid-cols-3 gap-3">
          {['mpesa', 'cash', 'bank'].map((method) => (
            <button
              key={method}
              type="button"
              onClick={() => setFormData({ ...formData, payment_method: method })}
              className={`px-4 py-3 rounded-xl border-2 font-medium capitalize transition-all ${
                formData.payment_method === method
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {method}
            </button>
          ))}
        </div>
      </div>

      <Input
        label="Reference Number"
        name="reference_number"
        value={formData.reference_number}
        onChange={handleChange}
        error={errors.reference_number?.[0]}
        placeholder="Transaction ID or receipt number"
        helpText="Optional: MPesa code or receipt number"
      />

      <Input
        label="Contribution Date"
        name="contribution_date"
        type="date"
        value={formData.contribution_date}
        onChange={handleChange}
        error={errors.contribution_date?.[0]}
        required
      />

      <div>
        <label
          htmlFor="contribution-notes"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Notes
        </label>
        <textarea
          id="contribution-notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
          placeholder="Optional notes..."
        />
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} fullWidth disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" fullWidth loading={loading}>
          Record Contribution
        </Button>
      </div>
    </form>
  );
};

export default RecordContributionForm;
